/**
 * useEditSystems hook
 */

// React
import { ChangeEvent, useEffect, useState } from 'react';

// Service
import ContentSystemService from 'services/ContentSystemService';

// Libraries
import { Control, FieldErrors, useForm, UseFormHandleSubmit, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';

// Utils
import {
  AWS_S3,
  MAX_LENGTHS,
  MAX_FILE_SIZE,
  VALIDATION_MESSAGES,
  FILE_ALLOWED_FORMATS,
  NOTIFICATION_MESSAGES,
  TIME_AUTO_CLOSE_NOTIFY,
} from 'utils/constants';
import { DataSettingContentSystem } from 'utils/types';
import { parseMessage } from 'utils/helper/common';

// Recoil
import { useSetRecoilState } from 'recoil';
import { notifyAtom } from 'recoil/NotifyRecoil';
import { loadingAtom } from 'recoil/LoadingRecoil';

// Router
import ROUTES from '../../configs/route';

interface UseSettingContentSystemType {
  control: Control<DataSettingContentSystem, any>;
  handleSubmit: UseFormHandleSubmit<DataSettingContentSystem>;
  handleUpdate: (_data: DataSettingContentSystem) => void;
  handleClose: () => void;
  handleConfirmDelete: () => void;
  handleConfirmUpdate: () => Promise<void>;
  handleButtonDeleteImage: (_name: string) => void;
  handleButtonPreviewImage: (_image: string) => void;
  handleFileChange: (_e: ChangeEvent<HTMLInputElement>, _iconType: string) => void;
  formData: DataSettingContentSystem;
  modalConfirm: boolean;
  modalPreview: boolean;
  modalConfirmUpdate: boolean;
  errors: FieldErrors<DataSettingContentSystem>;
  imagePreview: string;
  messageErrorIcon: string;
  messageErrorIconActive: string;
  messageErrorIconDeactive: string;
  isShowValidateImage: boolean;
  notifyCustom: any;
}

const { COMMON } = VALIDATION_MESSAGES;

const schema = (totalServices: number) =>
  yup
    .object({
      title: yup
        .string()
        .max(MAX_LENGTHS.MAX_15, COMMON.MAX_LENGTH_SYSTEM(MAX_LENGTHS.MAX_15))
        .required(VALIDATION_MESSAGES.SYSTEM.NAME.REQUIRED),
      position: yup
        .mixed()
        .required(VALIDATION_MESSAGES.SYSTEM.POSITION.REQUIRED)
        .transform((value) => {
          if (value === undefined || value === null || value === '') {
            return undefined;
          }
          const num = Number(value);
          return isNaN(num) ? value : num;
        })
        .test('isNumber', VALIDATION_MESSAGES.SYSTEM.POSITION.INVALID, (value) => {
          return typeof value === 'number' && Number.isInteger(value);
        })
        .test(
          'range',
          parseMessage(VALIDATION_MESSAGES.SYSTEM.POSITION.RANGE, [totalServices]),
          (value) => {
            return typeof value === 'number' && value <= Number(totalServices) && value > 0;
          }
        ),
      body: yup
        .string()
        .max(MAX_LENGTHS.MAX_46, COMMON.MAX_LENGTH_SYSTEM(MAX_LENGTHS.MAX_46))
        .required(VALIDATION_MESSAGES.SYSTEM.DESCRIPTION.REQUIRED),
      callback_url: yup
        .string()
        .required(VALIDATION_MESSAGES.SYSTEM.URL.REQUIRED)
        .max(MAX_LENGTHS.MAX_100, COMMON.MAX_LENGTH_SYSTEM(MAX_LENGTHS.MAX_100))
        .matches(/^(https?:\/\/)/, VALIDATION_MESSAGES.SYSTEM.URL.FORMAT),
      color: yup
        .string()
        .required(VALIDATION_MESSAGES.SYSTEM.COLOR.REQUIRED)
        .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, VALIDATION_MESSAGES.SYSTEM.COLOR.INVALID),
    })
    .required() as any;

export const useSettingContentSystem = (serviceId: number): UseSettingContentSystemType => {
  const initData = {
    id: 0,
    is_displayed: '1',
    position: 0,
    title: '',
    body: '',
    callback_url: '',
    color: '',
    icon: null,
    icon_status_active: null,
    icon_status_deactive: null,
    icon_upload: null,
    icon_status_active_upload: null,
    icon_status_deactive_upload: null,
    icon_upload_size: 0,
    icon_status_active_upload_size: 0,
    icon_status_deactive_upload_size: 0,
  };

  const navigate = useNavigate();
  const [formData, setFormData] = useState<DataSettingContentSystem>(initData);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageDelete, setImageDelete] = useState<string>('');
  const setIsLoading = useSetRecoilState(loadingAtom);
  const [messageErrorIcon, setMessageErrorIcon] = useState('');
  const [messageErrorIconActive, setMessageErrorIconActive] = useState('');
  const [messageErrorIconDeactive, setMessageErrorIconDeactive] = useState('');
  const [isShowValidateImage, setIsShowValidateImage] = useState(false);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalPreview, setModalPreview] = useState(false);
  const [modalConfirmUpdate, setModalConfirmUpdate] = useState(false);
  const setNotify = useSetRecoilState(notifyAtom);
  const [notifyCustom, setNotifyCustom] = useState({
    open: false,
    severity: 'success',
    title: '',
  });
  const [totalServices, setTotalServices] = useState(0);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm<DataSettingContentSystem>({
    resolver: yupResolver<DataSettingContentSystem>(schema(totalServices)),
    mode: 'all',
  });

  const title = useWatch({
    control,
    name: 'title',
  });

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      title: title || '',
    }));
  }, [title]);

  const fetchDataAsync = async () => {
    setIsLoading(true);
    try {
      const data = await ContentSystemService.getService(serviceId);
      setTotalServices(data?.total_service || 0);
      setFormData({
        ...data,
        icon: `${AWS_S3.BASE_URL}/${data?.icon}`,
        icon_status_active: `${AWS_S3.BASE_URL}/${data?.icon_status_active}`,
        icon_status_deactive: `${AWS_S3.BASE_URL}/${data?.icon_status_deactive}`,
      });
      Object.entries(data).forEach(([key, value]) => {
        setValue(
          key as keyof DataSettingContentSystem,
          value as string | number | File | null | undefined
        );
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (notifyCustom.open) {
      timer = setTimeout(() => {
        handleClose();
      }, TIME_AUTO_CLOSE_NOTIFY);
    }

    return () => clearTimeout(timer);
  }, [notifyCustom.open]);

  useEffect(() => {
    fetchDataAsync();
  }, [serviceId]);

  const handleUpdate = () => {
    if (messageErrorIcon || messageErrorIconActive || messageErrorIconDeactive) {
      setIsShowValidateImage(true);
      return;
    }
    setModalConfirmUpdate(true);
  };

  const handleClose = () => {
    setModalConfirm(false);
    setModalPreview(false);
    setModalConfirmUpdate(false);
    setNotifyCustom((prev) => ({ ...prev, open: false }));
  };

  const handleConfirmUpdate = async () => {
    try {
      setIsLoading(true);
      const data = {
        title: watch('title'),
        body: watch('body'),
        callback_url: watch('callback_url'),
        position: watch('position'),
        color: watch('color'),
        is_displayed: watch('is_displayed'),
        icon: formData?.icon_upload || null,
        icon_status_active: formData?.icon_status_active_upload || null,
        icon_status_deactive: formData?.icon_status_deactive_upload || null,
      };
      await ContentSystemService.updateService(formData.id, data);
      setNotify({ open: true, title: NOTIFICATION_MESSAGES.SUCCESS.UPDATE });
    } catch (error) {
      setNotify({ open: true, severity: 'error', title: NOTIFICATION_MESSAGES.ERROR.UPDATE });
      console.error(error);
    } finally {
      setIsLoading(false);
      navigate(ROUTES.SYSTEM_APPLICATION_SETTING);
    }
  };

  const handleConfirmDelete = () => {
    setFormData({
      ...formData,
      [`${imageDelete}`]: null,
    });

    switch (imageDelete) {
      case 'icon':
        setMessageErrorIcon(VALIDATION_MESSAGES.SYSTEM.ICON.REQUIRED);
        break;
      case 'icon_status_active':
        setMessageErrorIconActive(VALIDATION_MESSAGES.SYSTEM.ICON.REQUIRED);
        break;
      case 'icon_status_deactive':
        setMessageErrorIconDeactive(VALIDATION_MESSAGES.SYSTEM.ICON.REQUIRED);
    }
    setModalConfirm(false);
    setNotifyCustom({
      open: true,
      severity: 'success',
      title: NOTIFICATION_MESSAGES.SUCCESS.DELETE,
    });
  };

  const handleButtonDeleteImage = (name: string) => {
    try {
      setImageDelete(name);
      setModalConfirm(true);
    } catch (error) {
      setNotifyCustom({
        open: true,
        severity: 'error',
        title: NOTIFICATION_MESSAGES.ERROR.DELETE,
      });
    }
  };

  const handleButtonPreviewImage = (image: string) => {
    setImagePreview(image);
    setModalPreview(true);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>, imageName: string) => {
    const fileConfig: any = {
      icon_upload: {
        setError: setMessageErrorIcon,
        uploadKey: 'icon_upload',
      },
      icon_status_active_upload: {
        setError: setMessageErrorIconActive,
        uploadKey: 'icon_status_active_upload',
      },
      icon_status_deactive_upload: {
        setError: setMessageErrorIconDeactive,
        uploadKey: 'icon_status_deactive_upload',
      },
    };

    try {
      const file = event.target.files?.[0];
      if (file) {
        const fileName = `${imageName}_upload`;
        if (file.size > MAX_FILE_SIZE) {
          fileConfig[fileName].setError(VALIDATION_MESSAGES.SYSTEM.ICON.MAX_SIZE);
          setIsShowValidateImage(true);
        } else if (!FILE_ALLOWED_FORMATS.includes(file.type)) {
          fileConfig[fileName].setError(VALIDATION_MESSAGES.SYSTEM.ICON.EXTENSION);
          setIsShowValidateImage(true);
        } else {
          switch (fileName) {
            case 'icon_upload':
              formData[`icon_upload`] = file;
              setMessageErrorIcon('');
              break;
            case 'icon_status_active_upload':
              formData[`icon_status_active_upload`] = file;
              setMessageErrorIconActive('');
              break;
            case 'icon_status_deactive_upload':
              formData[`icon_status_deactive_upload`] = file;
              setMessageErrorIconDeactive('');
          }

          setFormData({
            ...formData,
            [`${imageName}`]: URL.createObjectURL(file),
          });
          setNotifyCustom({
            open: true,
            severity: 'success',
            title: NOTIFICATION_MESSAGES.SUCCESS.UPLOAD,
          });
        }
      }
    } catch (error) {
      setNotifyCustom({
        open: true,
        severity: 'error',
        title: NOTIFICATION_MESSAGES.ERROR.UPLOAD,
      });
    }
  };

  return {
    control,
    handleSubmit,
    handleUpdate,
    handleClose,
    handleConfirmDelete,
    handleButtonDeleteImage,
    handleButtonPreviewImage,
    handleFileChange,
    handleConfirmUpdate,
    formData,
    modalConfirm,
    modalPreview,
    modalConfirmUpdate,
    errors,
    imagePreview,
    messageErrorIcon,
    messageErrorIconActive,
    messageErrorIconDeactive,
    isShowValidateImage,
    notifyCustom,
  };
};
